import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { LoginButton } from '../Auth/LoginButton';
import { useUser } from '../../contexts/UserContext';
import { cn } from '../../utils/cn';
import { supabase } from '../../lib/supabase';
import { mainNavigation } from '../Sidebar/navigation';

type NavHeaderVariant = 'light' | 'dark' | 'transparent';

type NavHeaderTheme = 'light-on-dark' | 'dark-on-light';

interface NavHeaderProps {
  className?: string;
  variant?: NavHeaderVariant;
  theme?: NavHeaderTheme;
  onMenuClick?: () => void;
}

export function NavHeader({ className, variant, theme = 'light-on-dark', onMenuClick }: NavHeaderProps) {
  const { currentResidence, residences, setCurrentResidence, isAdmin } = useUser();
  const location = useLocation();
  const [isHidden, setIsHidden] = useState(false);
  const [atTop, setAtTop] = useState(true);
  const lastScrollYRef = useRef(0);
  const [showAptMenu, setShowAptMenu] = useState(false);
  const [apartmentNumbers, setApartmentNumbers] = useState<Record<number, string>>({});

  // Avled variant fra theme hvis ikke spesifisert (må være før useEffect som bruker den)
  const resolvedVariant: NavHeaderVariant = variant
    ? variant
    : theme === 'dark-on-light'
      ? 'light'
      : 'dark';

  useEffect(() => {
    function onScroll() {
      const y = window.scrollY || 0;
      // Øk terskel for transparens på forsiden (variant 'transparent')
      const transparentThreshold = resolvedVariant === 'transparent' ? 240 : 8;
      setAtTop(y < transparentThreshold);

      const goingDown = y > lastScrollYRef.current;
      // Skjul ved scroll ned når man har beveget seg litt
      if (goingDown && y > 32) {
        setIsHidden(true);
      } else {
        setIsHidden(false);
      }
      lastScrollYRef.current = y;
    }

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll as any);
  }, [resolvedVariant]);

  // Hent leilighetsnumre for alle tilknytninger med apartment_id
  useEffect(() => {
    const apartmentIds = Array.from(new Set((residences || []).map(r => r.apartment_id).filter(Boolean) as number[]));
    if (apartmentIds.length === 0) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('apartment')
        .select('id, number')
        .in('id', apartmentIds as number[]);
      if (!error && !cancelled) {
        const map: Record<number, string> = {};
        (data || []).forEach((row: any) => { if (row?.id != null) map[row.id] = String(row.number ?? ''); });
        setApartmentNumbers(map);
      }
    })();
    return () => { cancelled = true; };
  }, [residences]);

  const getBackgroundColor = () => {
    switch (resolvedVariant) {
      case 'light':
        return 'bg-condo-light';
      case 'dark':
        return 'bg-condo-dark';
      case 'transparent':
        // Bruk overlay for bakgrunn og hold selve headeren transparent
        return 'bg-transparent';
      default:
        return 'bg-condo-dark';
    }
  };

  const getTextColor = () => {
    switch (resolvedVariant) {
      case 'light':
        return 'text-condo-dark';
      case 'dark':
        return 'text-condo-light';
      case 'transparent':
        return 'text-condo-light drop-shadow-[0_2px_2px_rgba(0,0,0,0.3)]';
      default:
        return 'text-condo-light';
    }
  };

  const getResidenceTypeText = (type: 'condo' | 'cooperative' | null | undefined) => {
    if (type === 'condo') return 'Sameiet';
    if (type === 'cooperative') return 'Borettslaget';
    return 'Bofellesskapet';
  };

  function isActive(path: string): boolean {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  }

  // Filtrer navigasjon basert på brukerens tilgang
  const shouldShowNavItem = (item: typeof mainNavigation[0]) => {
    if (!item.roles) return true;
    
    return item.roles.some(role => {
      if (role === 'admin') return isAdmin;
      if (role === 'board') {
        // Sjekk om brukeren har en board-rolle i noen av sine tilhørigheter
        return isAdmin || currentResidence?.role === 'board' || residences.some(r => r.role === 'board');
      }
      // For owner/tenant, sjekk currentResidence eller alle residences
      return currentResidence?.role === role || residences.some(r => r.role === role);
    });
  };

  const filteredNavigation = mainNavigation.filter(shouldShowNavItem);

  return (
    <>
      {/* Desktop header */}
      <header className={cn(
        'hidden lg:flex items-center justify-between px-8 h-16 fixed top-0 left-0 right-0 z-40 transition-colors transition-transform duration-1000 ease-in-out',
        getBackgroundColor(),
        isHidden ? '-translate-y-full' : 'translate-y-0',
        className
      )}>
        {resolvedVariant === 'transparent' && (
          <div
            className={cn(
              'absolute inset-0 pointer-events-none transition-opacity duration-1000 ease-in-out',
              atTop ? 'opacity-0' : 'opacity-100'
            )}
            style={{ backgroundColor: 'rgb(0,87,80)' }}
          />
        )}
        <Link to="/" className="flex items-center relative z-10">
          <img
            src={
              (location.pathname.startsWith('/admin') || location.pathname.startsWith('/styret'))
                ? "/images/Condo-logo-dark.png"
                : "/images/Condo-logo-light.png"
            }
            alt="Condo Logo"
            className="h-8"
          />
        </Link>
        
        <div className="flex items-center gap-8">
          <nav className="flex items-center gap-6">
            {filteredNavigation.map((item) => {
              // Spesialhåndtering for "Min bolig" med flere leiligheter
              if (item.to === '/min-bolig') {
                const apartmentBelongs = (residences || []).filter(r => r.apartment_id);
                const multiple = apartmentBelongs.length > 1;
                
                if (multiple) {
                  const sameResidence = new Set(apartmentBelongs.map(b => b.residence_id)).size === 1;
                  const options = apartmentBelongs.map(b => {
                    const aptLabel = apartmentNumbers[b.apartment_id as number] || String(b.apartment_id);
                    const resName = b.residence?.name || '';
                    return {
                      key: `${b.residence_id}-${b.apartment_id}-${b.role}`,
                      label: sameResidence ? `${aptLabel}` : `${resName}, ${aptLabel}`,
                      belong: b
                    };
                  });
                  
                  return (
                    <div key={item.to} className="relative">
                      <button
                        onClick={() => setShowAptMenu(v => !v)}
                        className={cn('font-medium transition-opacity pb-1 border-b-2', getTextColor(), isActive('/min-bolig') ? 'border-current' : 'border-transparent hover:opacity-80')}
                      >
                        {item.label} ▾
                      </button>
                      {showAptMenu && (
                        <div className="absolute mt-2 bg-white rounded-md shadow-lg border min-w-[12rem] z-50">
                          <ul className="py-1">
                            {options.map(opt => (
                              <li key={opt.key}>
                                <button
                                  onClick={() => { setCurrentResidence(opt.belong as any); setShowAptMenu(false); }}
                                  className="w-full text-left px-3 py-2 hover:bg-gray-100 text-gray-800"
                                >
                                  {opt.label}
                                </button>
                              </li>
                            ))}
                            <li>
                              <Link to="/min-bolig" className="block px-3 py-2 text-sm text-condo-dark hover:underline">
                                Gå til Min bolig
                              </Link>
                            </li>
                          </ul>
                        </div>
                      )}
                    </div>
                  );
                }
              }
              
              // Spesialhåndtering for "Bofellesskapet" med dynamisk navn
              const label = item.to === '/bofellesskapet' 
                ? getResidenceTypeText(currentResidence?.residence.type)
                : item.label;
              
              return (
                <Link 
                  key={item.to}
                  to={item.to} 
                  className={cn('font-medium transition-opacity pb-1 border-b-2', getTextColor(), isActive(item.to) ? 'border-current' : 'border-transparent hover:opacity-80')}
                >
                  {label}
                </Link>
              );
            })}
          </nav>
          <div className="flex items-center">
            <div className={cn(
              'rounded-full',
              resolvedVariant === 'light' ? 'bg-condo-dark/20' : 'bg-condo-light/30'
            )}>
              <LoginButton variant={resolvedVariant} />
            </div>
          </div>
        </div>
      </header>

      {/* Mobile header */}
      <header className={cn(
        'lg:hidden fixed top-0 left-0 right-0 h-16 z-50 transition-all duration-1000 ease-in-out',
        isHidden ? '-translate-y-full' : 'translate-y-0'
      )}>
        {/* Bakgrunnsoverlay som vises når man scroller ned */}
        <div
          className={cn(
            'absolute inset-0 pointer-events-none transition-opacity duration-1000 ease-in-out',
            // Hjem-siden: transparent øverst, bakgrunn ved scroll
            (location.pathname === '/' && atTop) 
              ? 'opacity-0' 
              : (location.pathname.startsWith('/admin') || location.pathname.startsWith('/styret'))
                ? getBackgroundColor()
                : 'bg-condo-dark'
          )}
        />
        {/* Ekstra bakgrunn for å dekke padding-området */}
        <div
          className={cn(
            'absolute inset-0 pointer-events-none transition-opacity duration-1000 ease-in-out',
            // Hjem-siden: transparent øverst, bakgrunn ved scroll
            (location.pathname === '/' && atTop) 
              ? 'opacity-0' 
              : (location.pathname.startsWith('/admin') || location.pathname.startsWith('/styret'))
                ? getBackgroundColor()
                : 'bg-condo-dark'
          )}
          style={{ margin: '8px', borderRadius: '8px' }}
        />
        <div className="h-full flex items-center justify-between px-3 pt-4 pb-8 m-2 relative z-10">
          <Link to="/" className="flex items-center flex-shrink-0">
            <img
              src={
                (location.pathname.startsWith('/admin') || location.pathname.startsWith('/styret'))
                  ? "/images/Condo-logo-dark.png"
                  : "/images/Condo-logo-light.png"
              }
              alt="Condo Logo"
              className="h-8"
            />
          </Link>
          <button
            onClick={onMenuClick || (() => {})}
            className={cn(
              'p-2 transition-colors rounded-lg flex-shrink-0',
              (location.pathname.startsWith('/admin') || location.pathname.startsWith('/styret'))
                ? 'text-condo-dark hover:bg-black/5'
                : 'text-condo-light hover:bg-white/10'
            )}
            aria-label="Åpne meny"
          >
            <Menu className="h-7 w-7" />
          </button>
        </div>
      </header>
    </>
  );
}

